import { type AlignedHistory } from '#shared/types';
import type { Column } from '#client/components/ui/DataTable';
import { cn } from '#client/lib/cn';
import { formatDate, dash } from '#client/lib/format';
import type { ComparisonRow } from './patient-trends.logic';
import { TrendArrow } from './TrendArrow';
import { deltaBetween } from './result-trend.logic';

/** One visit's result, with its trend against the visit before it. */
function VisitCell({ row, index }: { row: ComparisonRow; index: number }) {
  const cell = row.cells[index];
  if (!cell) return <span className="c-muted">—</span>;

  const next = row.cells[index + 1];
  const delta =
    next &&
    deltaBetween(
      { result: cell.result, unit: cell.unit },
      { result: next.result, unit: next.unit },
    );

  return (
    <span className="c-history__cell">
      <span className={cn(cell.flag && `c-history__flag--${cell.flag}`)}>{cell.result}</span>
      {delta && <TrendArrow delta={delta} />}
      {cell.range_changed && (
        <abbr className="c-history__marker" title={`Reference range then: ${cell.normal_range}`}>
          *
        </abbr>
      )}
    </span>
  );
}

const PARAMETER_COLUMN: Column<ComparisonRow> = {
  key: 'parameter',
  header: 'Parameter',
  // The widest table in the app, and `frozen` exists exactly for it — an
  // operator scrolling sideways on a phone must always know which row
  // they are reading.
  frozen: true,
  width: '14rem',
  render: (row) => (
    <span className={row.retired ? 'c-history__retired-name' : undefined}>
      {row.name}
      {row.retired && <small> · no longer on this test</small>}
    </span>
  ),
};

const REFERENCE_COLUMN: Column<ComparisonRow> = {
  key: 'reference',
  header: 'Reference',
  width: '10rem',
  // The *current* visit's range. Per-visit ranges cannot live inside a
  // cell — cells never wrap and row height is `--row-h` — so a differing
  // range is a marker plus the footnote below.
  render: (row) => <span className="c-muted">{dash(row.reference)}</span>,
};

/**
 * Newest first, matching the printed sheet: the current visit, then the
 * earlier ones.
 */
export function buildComparisonColumns(visits: AlignedHistory['visits']): Column<ComparisonRow>[] {
  const visitColumns = visits.map((visit, index): Column<ComparisonRow> => ({
    key: `visit-${visit.report_id}`,
    header: formatDate(visit.visited_at),
    width: '8rem',
    render: (row) => <VisitCell row={row} index={index} />,
  }));

  return [PARAMETER_COLUMN, ...visitColumns, REFERENCE_COLUMN];
}
